import { createContext, useContext, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { toast } from "sonner";

export interface Plan {
  id: string;
  name: string;
  price: number;
  interval: string;
  max_members: number | null;
  features: string[];
  stripe_price_id: string | null;
}

export interface Subscription {
  id: string;
  church_id: string;
  plan_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  status: string;
  trial_ends_at: string | null;
  trial_used_at?: string | null;
  current_period_start: string;
  current_period_end: string;
  discount_percent?: number;
  discounted_price?: number | null;
  plans?: Plan;
}

interface SubscriptionContextType {
  plans: Plan[];
  subscription: Subscription | null;
  loading: boolean;
  isActive: boolean;
  isTrial: boolean;
  daysRemaining: number;
  memberCount: number;
  memberLimit: number | null;
  isAtMemberLimit: boolean;
  subscribe: (planId: string, promoCodeId?: string, discountPercent?: number, isYearly?: boolean) => Promise<void>;
  changePlan: (planId: string, isYearly?: boolean) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  reactivate: (planId: string, promoCodeId?: string, discountPercent?: number, isYearly?: boolean) => Promise<void>;
  validatePromoCode: (code: string) => Promise<{ id: string; code: string; discount_percent: number } | null>;
  getBillingPortalUrl: () => Promise<string | null>;
  openBillingPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const { profile, loading: churchLoading } = useChurch();
  const queryClient = useQueryClient();

  const resolveChurchId = async (): Promise<string | null> => {
    if (profile?.id) return profile.id;
    if (!session?.user?.id) return null;

    const { data: roleRow, error } = await supabase
      .from("user_roles")
      .select("church_id")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (error) throw error;
    return (roleRow as { church_id?: string | null } | null)?.church_id ?? null;
  };

  const extractFunctionErrorMessage = async (error: unknown, data: any, fallback: string): Promise<string> => {
    if (typeof data?.error === "string" && data.error.trim()) return data.error;
    if (typeof data?.message === "string" && data.message.trim()) return data.message;

    const e = error as any;
    const direct = typeof e?.message === "string" ? e.message : "";

    // Supabase FunctionsHttpError can expose `context` as a Response object.
    if (e?.context && typeof e.context.json === "function") {
      try {
        const body = await e.context.json();
        if (typeof body?.error === "string" && body.error.trim()) return body.error;
      } catch {
        // ignore body parsing errors
      }
    }

    const contextText = typeof e?.context === "string" ? e.context : "";
    if (contextText) {
      try {
        const parsed = JSON.parse(contextText);
        if (typeof parsed?.error === "string" && parsed.error.trim()) return parsed.error;
      } catch {
        // ignore parsing failures and continue
      }
    }

    const status = typeof e?.context?.status === "number" ? e.context.status : null;
    if (status === 404) {
      return "No active Stripe customer record found for this church. Please start a new checkout plan first.";
    }
    if (status === 401) {
      return "Your session expired. Please sign in again and retry.";
    }

    return direct || fallback;
  };

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plans")
        .select("*")
        .order("price", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((p) => ({
        ...p,
        max_members: (p as any).max_members ?? (p as any).member_limit ?? null,
        features: (Array.isArray(p.features) ? p.features : []) as string[],
      })) as Plan[];
    },
    enabled: !!session,
  });

  const { data: subscription = null, isLoading: subLoading } = useQuery({
    queryKey: ["subscription", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*, plans(*)")
        .eq("church_id", profile!.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        ...data,
        plans: data.plans
          ? {
              ...data.plans,
              features: (Array.isArray(data.plans.features) ? data.plans.features : []) as string[],
            }
          : undefined,
      } as Subscription;
    },
    enabled: !!session && !!profile?.id,
  });

  const { data: memberCount = 0 } = useQuery({
    queryKey: ["member_count", profile?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("members")
        .select("*", { count: "exact", head: true })
        .eq("church_id", profile!.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!session && !!profile?.id,
  });

  const loading = plansLoading || subLoading || churchLoading;

  const now = new Date();
  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end)
    : now;
  const daysRemaining = Math.max(
    0,
    Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  );

  const hasAccessUntilPeriodEnd =
    subscription?.status === "canceled" &&
    daysRemaining > 0;

  const isActive =
    subscription?.status === "active" ||
    (subscription?.status === "trialing" && daysRemaining > 0) ||
    hasAccessUntilPeriodEnd;

  const isTrial = subscription?.status === "trialing";

  const currentPlan = subscription?.plans;
  const memberLimit = currentPlan?.max_members ?? 5000;
  const isAtMemberLimit = memberLimit !== null && memberCount >= memberLimit;

  const validatePromoCode = async (code: string) => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return null;
    const { data, error } = await supabase
      .from("promo_codes")
      .select("id, code, discount_percent, max_uses, current_uses, expires_at, is_active")
      .eq("code", trimmed)
      .eq("is_active", true)
      .maybeSingle();
    if (error || !data) return null;
    if (data.expires_at && new Date(data.expires_at) < new Date()) return null;
    if (data.max_uses !== null && data.current_uses >= data.max_uses) return null;
    return { id: data.id, code: data.code, discount_percent: data.discount_percent };
  };

  const subscribe = async (planId: string, promoCodeId?: string, discountPercent?: number, isYearly = false) => {
    const churchId = await resolveChurchId();
    if (!churchId) {
      toast.error("Church profile is still linking. Please wait a moment and try again.");
      return;
    }

    // Keep church cache in sync for other screens that rely on useChurch().
    queryClient.invalidateQueries({ queryKey: ["church_profile", session?.user?.id] });

    toast.loading("Redirecting to checkout...", { id: "stripe-checkout" });
    const { data, error } = await supabase.functions.invoke("stripe-create-checkout", {
      body: {
        plan_id: planId,
        church_id: churchId,
        promo_code_id: promoCodeId,
        discount_percent: discountPercent,
        is_yearly: isYearly,
        success_url: `${window.location.origin}/billing?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/billing`,
      },
    });
    toast.dismiss("stripe-checkout");
    if (error || !data?.url) {
      const message = await extractFunctionErrorMessage(error, data, "Failed to start checkout. Please try again.");
      toast.error(message);
      return;
    }
    window.location.href = data.url;
  };

  const changePlan = async (planId: string, isYearly = false) => {
    const churchId = await resolveChurchId();
    if (!churchId) return;

    // If no Stripe linkage exists yet, start checkout to establish billing.
    if (!subscription?.stripe_subscription_id) {
      toast.message("Opening checkout to update your plan...");
      await subscribe(planId, undefined, undefined, isYearly);
      return;
    }

    toast.loading("Switching plan...", { id: "stripe-change-plan" });
    const { data, error } = await supabase.functions.invoke("stripe-change-plan", {
      body: {
        church_id: churchId,
        plan_id: planId,
        is_yearly: isYearly,
      },
    });
    toast.dismiss("stripe-change-plan");

    if (error || data?.error) {
      const message = await extractFunctionErrorMessage(
        error,
        data,
        "Failed to switch plan. Please try again.",
      );
      toast.error(message);
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ["subscription", churchId] });
    const formatAmount = (value: number) =>
      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);

    if (data?.change_type === "no_change") {
      toast.message("No changes needed. This plan and billing cycle are already active.");
      return;
    }

    if (data?.change_type === "downgrade") {
      const effectiveDate = data?.effective_date
        ? new Date(String(data.effective_date)).toLocaleDateString()
        : "next billing cycle";

      toast.message(`Plan change scheduled. Your new plan will start on ${effectiveDate}.`);
      return;
    }

    if (data?.change_type === "upgrade") {
      const prorationEstimate = Number(data?.proration_estimate_amount ?? 0);
      if (Number.isFinite(prorationEstimate) && prorationEstimate > 0) {
        toast.success(`Plan updated. Estimated prorated charge: ${formatAmount(prorationEstimate)}.`);
      } else {
        toast.success("Plan updated successfully.");
      }
      return;
    }

    toast.success("Plan change submitted successfully.");
  };

  const cancelSubscription = async () => {
    // Redirect to Stripe billing portal to handle cancellation authoritatively
    await openBillingPortal();
  };

  const reactivate = async (planId: string, promoCodeId?: string, discountPercent?: number, isYearly = false) => {
    const churchId = await resolveChurchId();
    if (!churchId) {
      toast.error("Church profile is still linking. Please wait a moment and try again.");
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["church_profile", session?.user?.id] });

    toast.loading("Redirecting to checkout...", { id: "stripe-checkout" });
    const { data, error } = await supabase.functions.invoke("stripe-create-checkout", {
      body: {
        plan_id: planId,
        church_id: churchId,
        promo_code_id: promoCodeId,
        discount_percent: discountPercent,
        is_yearly: isYearly,
        success_url: `${window.location.origin}/billing?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/billing`,
      },
    });
    toast.dismiss("stripe-checkout");
    if (error || !data?.url) {
      const message = await extractFunctionErrorMessage(error, data, "Failed to start checkout. Please try again.");
      toast.error(message);
      return;
    }
    window.location.href = data.url;
  };

  const getBillingPortalUrl = async () => {
    const churchId = await resolveChurchId();
    if (!churchId) {
      toast.error("Church profile is still linking. Please wait a moment and try again.");
      return null;
    }

    queryClient.invalidateQueries({ queryKey: ["church_profile", session?.user?.id] });

    const { data, error } = await supabase.functions.invoke("stripe-billing-portal", {
      body: {
        church_id: churchId,
        return_url: `${window.location.origin}/billing?portal=return`,
      },
    });
    if (error || !data?.url) {
      const message = await extractFunctionErrorMessage(error, data, "Failed to open billing portal. Please try again.");
      toast.error(message);
      return null;
    }

    return data.url as string;
  };

  const openBillingPortal = async () => {
    toast.loading("Opening billing portal...", { id: "billing-portal" });
    const url = await getBillingPortalUrl();
    toast.dismiss("billing-portal");
    if (!url) return;

    window.location.href = url;
  };

  return (
    <SubscriptionContext.Provider
      value={{
        plans,
        subscription,
        loading,
        isActive,
        isTrial,
        daysRemaining,
        memberCount,
        memberLimit,
        isAtMemberLimit,
        subscribe,
        changePlan,
        cancelSubscription,
        reactivate,
        validatePromoCode,
        getBillingPortalUrl,
        openBillingPortal,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) throw new Error("useSubscription must be used within SubscriptionProvider");
  return context;
}
